import React from "react";
import PropTypes from "prop-types";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { Grid, Alert, Link, Chip, Box, Button } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { format, isValid } from "date-fns";

import downloadExamInstancesCsv from "../utils/downloadExamInstancesCsv";

const formatDateTime = (dateTime) => {
  if (isValid(dateTime)) {
    return format(dateTime, "yyyy-MM-dd hh:mm a");
  }
  return null;
};

const formatTags = (tags) => {
  return tags.map((t, i) => <Chip key={i} label={t.name} variant="outlined" />);
};

const formatTagsCsv = (tags) =>
  (tags || []).map((tag) => tag.name).join("; ");

const columns = [
  {
    field: "id",
    headerName: "ID",
    width: 80,
    renderCell: (params) => (
      <Link href={`/thebai/exam-instances/${params.row.id}/admin-report`}>
        <b>{params.row.id}</b>
      </Link>
    ),
  },
  {
    field: "examineeName",
    headerName: "Examinee Name",
    width: 250,
  },
  {
    field: "examineeEmail",
    headerName: "Examinee Email",
    width: 250,
  },
  {
    field: "examDesignName",
    headerName: "Exam Design",
    width: 250,
  },
  {
    field: "score",
    headerName: "Score",
    width: 130,
    valueGetter: (value) =>
      value != null ? Number(value).toFixed(2) : "",
  },
  {
    field: "is_reviewed",
    headerName: "Reviewed",
    width: 130,
    valueGetter: (value) => (value ? "Yes" : "No"),
    renderCell: (params) => (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
        }}
      >
        {params.row.is_reviewed ? "✅" : "❌"}
      </div>
    ),
  },
  {
    field: "startTime",
    headerName: "Start Time",
    width: 180,
    valueGetter: (value) => formatDateTime(value) || "",
  },
  {
    field: "finishTime",
    headerName: "Finish Time",
    width: 180,
    valueGetter: (value) => formatDateTime(value) || "",
  },
  {
    field: "tags",
    headerName: "Tags",
    width: 250,
    valueGetter: (value) => formatTagsCsv(value),
    renderCell: (params) => <>{formatTags(params.row.tags)}</>,
  },
  {
    field: "availableFrom",
    headerName: "Available From",
    width: 180,
    valueGetter: (value) => formatDateTime(value) || "",
  },
  {
    field: "availableUntil",
    headerName: "Available Until",
    width: 180,
    valueGetter: (value) => formatDateTime(value) || "",
  },
  {
    field: "createdBy",
    headerName: "Created By",
    width: 250,
  },
  {
    field: "createdOn",
    headerName: "Created On",
    width: 250,
    valueGetter: (value) => formatDateTime(value) || "",
  },
];

export const ExamInstancesTable = ({ examInstances, hasSearched }) => {
  const handleDownloadCsv = () => {
    downloadExamInstancesCsv(examInstances);
  };

  return (
    <Grid>
      <br />
      {!hasSearched ? null : examInstances.length === 0 ? (
        <Alert variant="filled" severity="warning">
          No questions found for this filter criteria.
        </Alert>
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mb: 1,
            }}
          >
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadCsv}
              sx={{ textTransform: "none" }}
            >
              Download CSV
            </Button>
          </Box>
          <div
            style={{
              height: "80vh",
              width: "100%",
            }}
          >
            <DataGrid
              columns={columns}
              rows={examInstances}
              getRowId={(row) => row.id}
              disableRowSelectionOnClick
              slots={{
                toolbar: GridToolbar,
              }}
              slotProps={{
                toolbar: {
                  csvOptions: {
                    fileName: "exam-instances-search-results",
                    utf8WithBom: true,
                  },
                },
              }}
            />
          </div>
        </>
      )}
    </Grid>
  );
};

ExamInstancesTable.propTypes = {
  examInstances: PropTypes.array.isRequired,
  hasSearched: PropTypes.bool.isRequired,
};
